import Axios from 'axios';
import React, { useEffect, Fragment, useState, useRef } from 'react';
import { Table, Button, Modal, Input, Select } from 'antd';
import Vimeo from '@u-wave/react-vimeo';
// ページ外郭コンテナ（デザインシステム Ph.4: width:90% → max-width 1240px へ統一）
// テーブル強化 PJ Ph.3: 列リサイズ + ソート + 省略
import {
  PageContainer,
  useResizableColumns,
  ellipsisColumn,
  textSorter,
} from '../../common';
// import { surveyCode } from '../../Config/surveyCode';

const VideoMgrPage = (props) => {
  // console.log({ props });
  const { user } = props;
  const [videoInfo, setVideoInfo] = useState([]);
  const [viewModal, setViewModal] = useState(false);
  const [videoId, setVideoId] = useState('');
  const [paused, setPaused] = useState(false);
  const playerRef = useRef(null); // Vimeoプレイヤーの参照を保持
  // 検索条件（動画名 = 部分一致、カテゴリー・講師 = 完全一致の絞り込み）
  const [search, setSearch] = useState({
    title: '',
    cName: undefined,
    iName: undefined,
  });

  const readDB = async () => {
    try {
      if (!user.userData) return null;
      // console.log(user.userData.isAuth);
      if (user.userData.isAuth) {
        const res = await Axios.get('/api/video/');
        // console.log(res.data.groups);
        if (res.data.success) {
          let vArr = res.data.video;
          vArr.map((element) => {
            element.iName = element.instructor.name;
            element.cName = element.category_code_id.category_name;
            element.isThumbnail = element.thumbnail[0] ? '⭕️' : '❌';
            return element;
          });
          // console.log(vArr);
          setVideoInfo(vArr);
        } else {
          throw new Error('動画データ取得に失敗しました。');
        }
      } else {
        throw new Error('ログイン前です。');
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    readDB();
    // console.log({ mySurvey });
  }, [user]);

  const editVideoHandler = async (vInfo, e) => {
    e.preventDefault();
    try {
      vInfo.fg = 'edit';
      props.history.push({
        pathname: '/videoedit',
        state: vInfo,
      });
    } catch (error) {
      console.log(error);
    }
  };
  const newVideoHandler = async (e) => {
    e.preventDefault();
    try {
      props.history.push({
        pathname: '/videoupload',
        state: { fg: 'new' },
      });
    } catch (error) {
      console.log(error);
    }
  };
  // モーダルを閉じる前にビデオを完全に停止
  const closeModal = () => {
    // プレイヤーが存在する場合、明示的に一時停止
    if (playerRef.current) {
      try {
        playerRef.current.pause();
        playerRef.current.unload(); // ビデオを完全にアンロード
      } catch (error) {
        console.log('ビデオ停止エラー:', error);
      }
    }

    // プレイヤー参照をクリア
    playerRef.current = null;

    // 状態をリセット
    setVideoId(''); // ビデオIDをリセット
    setPaused(false); // paused状態を初期化

    // 少し遅延してモーダルを閉じる
    setTimeout(() => {
      setViewModal(false);
    }, 100);
  };

  // モーダルでのOK
  const handleOk = (e) => {
    closeModal();
  };

  const handleCancel = (e) => {
    closeModal();
  };

  // カードクリック時の処理 - 新しいビデオを設定してモーダルを開く
  const handleCardClick = (v) => {
    // console.log({ v });
    setVideoId(v.url.split('/')[2]);
    setPaused(false);
    setViewModal(true);
    // }
  };

  // Vimeoプレイヤーの準備完了時の処理
  const handleReady = (player) => {
    playerRef.current = player;
  };

  // 検索条件をクリア
  const clearSearch = () => {
    setSearch({ title: '', cName: undefined, iName: undefined });
  };

  // カテゴリー・講師の選択肢（重複排除）
  const categoryOptions = Array.from(
    new Set(videoInfo.map((v) => v.cName).filter(Boolean))
  ).map((c) => ({ value: c, label: c }));
  const instructorOptions = Array.from(
    new Set(videoInfo.map((v) => v.iName).filter(Boolean))
  ).map((i) => ({ value: i, label: i }));

  // 絞り込み後の一覧（動画名は部分一致、カテゴリー・講師は完全一致）
  const filteredVideoInfo = videoInfo.filter((v) => {
    const byTitle =
      !search.title ||
      (v.title || '').toLowerCase().includes(search.title.toLowerCase());
    const byCategory = !search.cName || v.cName === search.cName;
    const byInstructor = !search.iName || v.iName === search.iName;
    return byTitle && byCategory && byInstructor;
  });

  // テーブル強化 PJ Ph.3: <Column> 子要素 → columns 配列。テキスト列はソート + 省略、
  // 操作 2 列（編集 / 動画確認）は既存ボタンを維持。列幅ドラッグ・初期フィット・
  // 横スクロールは useResizableColumns が配線。
  const rawColumns = [
    ellipsisColumn({
      title: '動画名',
      dataIndex: 'title',
      key: 'title',
      width: 240,
      sorter: textSorter('title'),
    }),
    ellipsisColumn({
      title: 'カテゴリー',
      dataIndex: 'cName',
      key: 'cName',
      width: 160,
      sorter: textSorter('cName'),
    }),
    ellipsisColumn({
      title: '講師',
      dataIndex: 'iName',
      key: 'iName',
      width: 120,
      sorter: textSorter('iName'),
    }),
    ellipsisColumn({
      title: '動画説明：管理向け',
      dataIndex: 'video_description',
      key: 'video_description',
      width: 280,
      sorter: textSorter('video_description'),
    }),
    {
      title: 'サムネール',
      dataIndex: 'isThumbnail',
      key: 'thumbnail',
      width: 110,
      sorter: textSorter('isThumbnail'),
    },
    {
      title: '編集',
      key: 'action',
      width: 90,
      render: (text, record) => (
        <span>
          <Button
            type={'primary'}
            size="small"
            onClick={(e) => editVideoHandler(record, e)}
            disabled={false}
          >
            編集
          </Button>
        </span>
      ),
    },
    {
      title: '動画',
      key: 'view',
      width: 100,
      render: (text, record) => (
        <span>
          <Button
            type={'primary'}
            size="small"
            onClick={(e) => handleCardClick(record, e)}
            disabled={false}
          >
            動画確認
          </Button>
        </span>
      ),
    },
  ];

  const { columns, components, scroll, wrapRef } = useResizableColumns(rawColumns);

  return (
    <Fragment>
      {/* 大量テーブル（動画一覧・多列）ページのため fluid（全幅）。
          広幅モニターでデータ密度を確保（デザインシステム「大量テーブル」例外）。 */}
      <PageContainer fluid>
        {/* 検索ツール（画面上部に一列） */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--space-3)',
            alignItems: 'center',
            marginBottom: 'var(--space-4)',
          }}
        >
          <Input
            placeholder="動画名で検索"
            allowClear
            value={search.title}
            onChange={(e) =>
              setSearch({ ...search, title: e.target.value })
            }
            style={{ width: 240 }}
          />
          <Select
            placeholder="カテゴリーで検索"
            allowClear
            showSearch
            optionFilterProp="label"
            value={search.cName}
            onChange={(v) => setSearch({ ...search, cName: v })}
            options={categoryOptions}
            style={{ width: 240 }}
          />
          <Select
            placeholder="講師で検索"
            allowClear
            showSearch
            optionFilterProp="label"
            value={search.iName}
            onChange={(v) => setSearch({ ...search, iName: v })}
            options={instructorOptions}
            style={{ width: 240 }}
          />
          <Button onClick={clearSearch}>クリア</Button>
        </div>
        <div ref={wrapRef}>
          <Table
            columns={columns}
            components={components}
            scroll={scroll}
            dataSource={filteredVideoInfo}
            size="middle"
            rowKey="_id"
            pagination={{
              defaultPageSize: 100,
              showSizeChanger: true,
              pageSizeOptions: ['50', '100', '200', '500'],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} / ${total}件`,
            }}
          />
        </div>

        <Button
          type={'primary'}
          size="small"
          onClick={(e) => newVideoHandler(e)}
        >
          新規動画アップロード
        </Button>
      </PageContainer>
      {/* モーダルが開いているときのみレンダリング */}
      {viewModal && (
        <div>
          <Modal
            title="動画再生"
            open={viewModal}
            onOk={handleOk}
            onCancel={handleCancel}
            styles={{ body: { overflowX: 'scroll' } }}
            width={1000}
            footer={null}
            destroyOnHidden={true} // モーダルを閉じるときに子要素を完全に破棄
            maskClosable={false} // マスククリックで閉じるのを無効化
          >
            {videoId && ( // videoIdがある場合のみレンダリング
              <Vimeo
                key={`vimeo-${videoId}-${Date.now()}`} // ユニークなkeyを生成
                video={videoId}
                className="vimeo pc"
                responsive
                autoplay={true}
                speed={true}
                paused={paused}
                onReady={handleReady} // プレイヤー準備完了時の処理
              />
            )}
          </Modal>
        </div>
      )}
    </Fragment>
  );
};

export default VideoMgrPage;
